import Cookies from "js-cookie";
import { store } from "@root/store";
import { getAuthCookieName } from "@utils/auth";
import { RESPONSE_TYPES } from "@constants/response-types";
import { HttpService } from "@services/http";
import {
  prepareErrorResponse,
  prepareResponseObject,
} from "@services/http/response";
import { AUTH_RESET } from "@store/action-types";
import { LocalStorageService } from "../local-storage";
import { persistor } from "@root/App";
import { getBaseUrl } from "@root/store/selectors/features";

const localStorageService = new LocalStorageService();

export interface ISignup {
  email: string;
  password: string;
  username: string;
}
export class AuthService extends HttpService {
  signOut = async (): Promise<any> => {
    try {
      Cookies.remove(getAuthCookieName(process.env.REACT_APP_ENV));
      await localStorageService.remove("authToken");
      // localStorageService.clear();
      store.dispatch({ type: AUTH_RESET });
      await persistor.purge();
      window.location.reload();
    } catch (error) {
      throw prepareErrorResponse(error);
    }
  };

  signIn = async (
    baseAuthUrl: string,
    data: Record<string, TObject>,
    isSocial: boolean
  ): Promise<TObject> => {
    try {
      const apiResponse = await this.post(
        isSocial
          ? `${baseAuthUrl}/api/google/auth/verify`
          : `${baseAuthUrl}/api/user/signin`,
        data
      );

      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  signInAdmin = async (
    baseAuthUrl: string,
    data: Record<string, TObject>,
    isSocial: boolean
  ): Promise<TObject> => {
    try {
      const apiResponse = await this.post(
        isSocial
          ? `${baseAuthUrl}/api/google/auth/verify`
          : `${baseAuthUrl}/api/admin/signin`,
        data
      );

      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  signUp = async (baseUrl: string, data: ISignup): Promise<TObject> => {
    try {
      const apiResponse = await this.post(`${baseUrl}/api/user/signup`, data);

      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  fullSignUp = async (
    baseUrl: string,
    data: ISignup,
    token: string
  ): Promise<TObject> => {
    try {
      const apiResponse = await this.patch(
        `${baseUrl}/api/user/profile/${token}`,
        data
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  updateLanguage = async (
    baseUrl: string,
    language: "en" | "ko",
    token: string
  ): Promise<TObject> => {
    try {
      const apiResponse = await this.patch(
        `${baseUrl}/api/user/profile`,
        { language },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  resetPass = async (baseUrl: string, data: ISignup): Promise<TObject> => {
    try {
      const apiResponse = await this.post(
        `${baseUrl}/api/user/reset-password`,
        data
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  verifyResetPass = async (
    baseUrl: string,
    token: string
  ): Promise<TObject> => {
    try {
      const apiResponse = await this.get(
        `${baseUrl}/api/user/reset-password/verification/${token}`
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  setNewPassword = async (baseUrl: string, data: any): Promise<TObject> => {
    const { token, password } = data;
    try {
      const apiResponse = await this.patch(
        `${baseUrl}/api/user/new-password/${token}`,
        {
          password,
        }
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  deleteAccount = async (baseAuthUrl: string): Promise<TObject> => {
    try {
      const apiResponse = await this.delete(`${baseAuthUrl}/api/user`);

      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  resendEmail = async (baseUrl: string, payload: any): Promise<TObject> => {
    try {
      const apiResponse = await this.post(
        `${baseUrl}/api/user/resend-email`,
        payload
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };
}
